
import { useEffect, useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import {
  getCities,
  getCourses,
  getStates,
  postEnquiryForm,
} from '../../../utils/apis/enquiry_form/Enquiry_Form';
import { Select2Data } from '../../../utils/common';

/* ===================== TYPES ===================== */

type SelectOption = {
  label: string;
  value: number;
};

type LeadFormData = {
  name: string;
  email: string;
  contact_no: string;

  state_id?: SelectOption | null;
  city_id?: SelectOption | null;
  course_id?: SelectOption | null;
  specilization_id?: SelectOption | null;
};

/* ===================== COMPONENT ===================== */

const LeadCaptureForm = () => {
  const navigate = useNavigate();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LeadFormData>();

  const [statesData, setStatesData] = useState<SelectOption[]>([]);
  const [cities, setCities] = useState<SelectOption[]>([]);

  /* ===================== API CALLS ===================== */

  useEffect(() => {
    const fetchStates = async () => {
      const res = await getStates(1);
      if (res?.success) {
        setStatesData(await Select2Data(res.data, 'state_id'));
      }
    };
    fetchStates();
  }, []);

  const fetchCities = async (stateId: number) => {
    const res = await getCities(stateId);
    if (res?.success) {
      setCities(await Select2Data(res.data, 'city_id'));
    }
  };

  /* ===================== SUBMIT ===================== */

  const onSubmit = async (data: LeadFormData) => {
    const formData = new FormData();

    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('mobile', data.contact_no);

    if (data.state_id) {
      formData.append('state_id', String(data.state_id.value));
    }

    if (data.city_id) {
      formData.append('district_id', String(data.city_id.value));
    }

    if (data.course_id) {
      formData.append('course_id', String(data.course_id.value));
    }

    if (data.specilization_id) {
      formData.append('specilization_id', String(data.specilization_id.value));
    }

    // formData.append("source_name", "Profcyma_Google_MBA-WX");
    formData.append('source_name', 'Profcyma_Google_Ads_Dy_Patil_Mba_Degree');
    formData.append('api_key', 'profcyma-new-website');

    try {
      const res = await postEnquiryForm(formData);
      if (res?.success) {
        // ✅ Download brochure
        const link = document.createElement('a');
        link.href = '/assets/broucher/dpu_brocher_2027.pdf';
        link.download = 'Brochure.pdf'; // File name for user
        link.click();

        navigate('/thank-you');
      } else {
        toast.error("You've already submitted an inquiry.");
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
  };

  /* ===================== STATIC DATA ===================== */

  const Courses: SelectOption[] = [
    { value: 8, label: 'Diploma' },
    { value: 9, label: 'Graduate' },
    { value: 10, label: 'Post Graduate' },
  ];

  /* ===================== JSX ===================== */

  return (
    <div className="bg-card rounded-xl p-2">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          {/* NAME */}
          <Col md={12} className="p-1">
            <Form.Control
              placeholder="Enter Full Name"
              className={classNames({ 'is-invalid': errors.name })}
              {...register('name', {
                required: 'Name is required',
                pattern: {
                  value: /^[A-Za-z ]+$/,
                  message: 'Only letters allowed',
                },
              })}
            />
            {errors.name && <span className="text-danger">{errors.name.message}</span>}
          </Col>

          {/* EMAIL */}
          <Col md={12} className="p-1">
            <Form.Control
              type="email"
              placeholder="Enter Email"
              className={classNames({ 'is-invalid': errors.email })}
              {...register('email', {
                required: 'Email is required',
              })}
            />
            {errors.email && <span className="text-danger">{errors.email.message}</span>}
          </Col>

          {/* MOBILE */}
          <Col md={12} className="p-1">
            <Form.Control
              placeholder="Enter Mobile Number"
              maxLength={10}
              className={classNames({ 'is-invalid': errors.contact_no })}
              {...register('contact_no', {
                required: 'Mobile number required',
                pattern: {
                  value: /^\d{10}$/,
                  message: 'Enter valid 10 digit number',
                },
              })}
            />
            {errors.contact_no && <span className="text-danger">{errors.contact_no.message}</span>}
          </Col>

          {/* STATE */}
          <Col md={6} className="p-1">
            <Controller
              name="state_id"
              control={control}
              rules={{ required: 'Select State' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={statesData}
                  classNamePrefix="react-select"
                  className={classNames({
                    'is-invalid': errors.state_id,
                  })}
                  onChange={(option) => {
                    field.onChange(option);
                    setValue('city_id', null);
                    fetchCities(option!.value);
                  }}
                />
              )}
            />
            {errors.state_id && <span className="text-danger">{errors.state_id.message}</span>}
          </Col>

          {/* CITY */}
          <Col md={6} className="p-1">
            <Controller
              name="city_id"
              control={control}
              rules={{ required: 'Select City' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={cities}
                  classNamePrefix="react-select"
                  className={classNames({
                    'is-invalid': errors.city_id,
                  })}
                />
              )}
            />
            {errors.city_id && <span className="text-danger">{errors.city_id.message}</span>}
          </Col>

          {/* COURSE */}
          <Col md={12} className="p-1">
            <Controller
              name="course_id"
              control={control}
              rules={{ required: 'Select Qualification' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={Courses}
                  placeholder="Select Highest Qualification"
                  classNamePrefix="react-select"
                  className={classNames({
                    'is-invalid': errors.course_id,
                  })}
                />
              )}
            />
            {errors.course_id && <span className="text-danger">{errors.course_id.message}</span>}
          </Col>

          {/* SUBMIT */}
          <Col md={12} className="text-center mt-3">
            <button type="submit" className="common_red_btn">
              Submit Application
            </button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default LeadCaptureForm;
